/**
 * Billing cycles — where each card is in its cycle, and when to buy.
 *
 * The question this page exists to kill: *"which card's cycle is about to close,
 * and if I buy this now — or next week — when do I actually pay for it?"* Eight
 * cards with eight different answers is not something anyone should hold in
 * their head.
 *
 * Three views, each answering one part of that:
 *
 *   1. **The cycle clock** — every card as a ring, reset at the top, filled to
 *      today. Where each card is, at a glance; pick one for its countdown and
 *      its dates.
 *   2. **Plan a purchase date** — every card's cycles on one timeline, with a
 *      draggable "buy on" day and a ranking that re-sorts by days to pay.
 *   3. **The exact dates** — a table, because a picture is for shape and a
 *      table is for numbers.
 *
 * **Every projected date is labelled as projected** (§2.7). The projection is
 * honest arithmetic on the card's own statement history, computed server-side
 * (`CycleCard.path`); the reasoning is on hover. A card with no statements says
 * what to do about it rather than showing a number.
 */

import { useQuery } from "@tanstack/react-query";
import { CreditCard, Info } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CycleClock } from "@/components/cycles/CycleClock";
import { cardName } from "@/components/cycles/cycleMath";
import { PurchasePlanner } from "@/components/cycles/PurchasePlanner";
import { Button, EmptyState, ErrorState, Panel, PageHeader } from "@/components/ui/primitives";
import { cyclesApi, type CycleCard } from "@/lib/api/client";
import { formatDate } from "@/lib/format";

export function CyclesPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cycles"],
    queryFn: cyclesApi.list,
  });
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Panel>
          <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
        </Panel>
      </div>
    );
  }

  const cards = data?.cards ?? [];
  const known = cards.filter((c) => c.cycle_end !== null);
  const unknown = cards.filter((c) => c.cycle_end === null);
  // Soonest to close first is the API's order; that card is the default focus.
  const selected = known.find((c) => c.card_id === selectedId) ?? known[0];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={
          data
            ? `${formatDate(data.today)} · ${known.length} card${known.length === 1 ? "" : "s"} with a known cycle`
            : "Close and due dates"
        }
        title={
          <>
            Billing <em className="accent-serif">cycles</em>
          </>
        }
        description="Where each card is in its cycle, when it resets, and which card gives you longest to pay on any day — learned from each card's own statement history."
      />

      {isLoading || !data ? (
        <LoadingState />
      ) : cards.length === 0 ? (
        <Panel>
          <EmptyState
            icon={CreditCard}
            title="No active cards"
            body="Add a card and MyDeck works out its cycle from the first statement it parses."
            action={
              <Button asChild variant="primary" size="sm">
                <Link to="/cards">Add a card</Link>
              </Button>
            }
          />
        </Panel>
      ) : (
        <>
          {selected && (
            <CycleClock
              cards={known}
              today={data.today}
              selectedId={selected.card_id}
              onSelect={setSelectedId}
            />
          )}

          {unknown.length > 0 && <UnknownCards cards={unknown} />}

          {known.length > 0 && (
            <PurchasePlanner cards={known} today={data.today} caveat={data.float_caveat} />
          )}

          {known.length > 0 && <FactsTable cards={known} />}
        </>
      )}
    </div>
  );
}

/** Cards with no learnable cycle sink here rather than vanish — they need a statement. */
function UnknownCards({ cards }: { cards: CycleCard[] }) {
  return (
    <Panel className="flex items-start gap-3 p-4">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden />
      <div className="text-sm text-muted">
        <span className="text-ink">
          {cards.map((c) => `${cardName(c)} •${c.last4}`).join(", ")}
        </span>{" "}
        {cards.length === 1 ? "has" : "have"} no cycle yet.{" "}
        {cards[0]?.unavailable}
      </div>
    </Panel>
  );
}

/**
 * The exact figures. A timeline shows shape; this shows numbers you can read off
 * and compare down a column — hence tabular figures and right-aligned days.
 */
function FactsTable({ cards }: { cards: CycleCard[] }) {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-lg font-medium text-ink">The exact dates</h2>
      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-hairline text-left">
                <th className="label-micro px-5 py-2 font-normal">Card</th>
                <th className="label-micro px-3 py-2 font-normal">Cycle opened</th>
                <th className="label-micro px-3 py-2 font-normal">Closes</th>
                <th className="label-micro px-3 py-2 font-normal">Statement</th>
                <th className="label-micro px-3 py-2 font-normal">Bill due</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Cycle</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Grace</th>
                <th className="label-micro px-5 py-2 text-right font-normal">Float</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {cards.map((c) => (
                <tr key={c.card_id}>
                  <td className="px-5 py-2.5">
                    <Link
                      to={`/cards/${c.card_id}`}
                      className="text-ink hover:text-brass"
                      title={`${c.close_basis} ${c.due_basis}`}
                    >
                      {cardName(c)}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted">
                    {formatDate(c.cycle_start)}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2.5 tabular-nums text-ink"
                    title={c.close_basis}
                  >
                    {formatDate(c.cycle_end)}
                  </td>
                  {/* Two issuers date the statement after the cycle closes, so
                      this is its own column rather than a repeat of the close. */}
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted">
                    {formatDate(c.statement_expected_on)}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted"
                    title={c.due_basis}
                  >
                    {formatDate(c.due_expected_on)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-muted">
                    {c.cycle_length_days !== null ? `${c.cycle_length_days}d` : "—"}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-muted"
                    title={c.due_basis}
                  >
                    {c.grace_days !== null ? `${c.grace_days}d` : "—"}
                  </td>
                  <td className="px-5 py-2.5 text-right font-medium tabular-nums text-ink">
                    {c.float_days !== null ? `${c.float_days}d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-hairline px-5 py-3 text-xs text-faint">
          Closes, statement, bill due, grace and float are all projected from each card's own
          statement history — hover any of them for the rule and how many statements agree.
          A printed due date on an unpaid bill is shown per card above, where it can't be
          mistaken for one of these.
        </p>
      </Panel>
    </section>
  );
}

function LoadingState() {
  return (
    <div className="space-y-8">
      <Panel className="p-5">
        <div className="skeleton h-4 w-56" />
        <div className="mt-4 space-y-2.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-6 w-full" />
          ))}
        </div>
      </Panel>
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Panel key={i} className="p-5">
            <div className="skeleton h-4 w-40" />
            <div className="skeleton mt-4 h-4 w-full" />
            <div className="skeleton mt-3 h-3 w-2/3" />
          </Panel>
        ))}
      </div>
    </div>
  );
}
